import { useState, useEffect, useRef } from 'react'
import {
  Zap, FileText, Upload, Image as ImageIcon, CheckCircle, AlertTriangle,
  Download, Play, RefreshCw, Server, ArrowRight, Eye, ShieldAlert, Cpu,
  CheckCircle2, Clock, Sparkles, Activity, FileSpreadsheet, FileCode,
  FileType, Trash2, X, ChevronDown, ChevronUp, HardDrive, ShieldCheck,
  Database, Layers, FileCheck, Info, Plus, ImagePlus, Check, Key, Lock
} from 'lucide-react'
import {
  getClients, uploadMultiTelemetryFiles, uploadScreenshot,
  generateManualReport, downloadReport
} from '../api'
import { useAuth } from '../context/AuthContext'
import Brand3DCanvas from './Brand3DCanvas'

export default function QuickReport({ onNavigate }) {
  const { user } = useAuth()
  const [clients, setClients] = useState([])
  const [selectedClientId, setSelectedClientId] = useState('')

  // Report Identity & Period
  const [clientName, setClientName] = useState('')
  const [accountId, setAccountId] = useState('')
  const [reportDate, setReportDate] = useState(() => {
    return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  })
  const [monitoringPeriod, setMonitoringPeriod] = useState('Last 24 Hours (00:00 - 23:59 IST)')
  const preparedBy = user?.name || 'Lead DevOps Engineer'

  // Gemini API Key config
  const [geminiApiKey, setGeminiApiKey] = useState(() => {
    return localStorage.getItem('cloudiq.gemini_api_key') || ''
  })
  const [showApiKeyModal, setShowApiKeyModal] = useState(false)

  // Header Banners & Key Highlights
  const [bannerMsg, setBannerMsg] = useState('ALL MONITORED SYSTEMS OPERATIONAL & HEALTHY')
  const [alarmSummary, setAlarmSummary] = useState('0 IN ALARM | 8 OK')
  const [execSummary, setExecSummary] = useState('')
  const [execBullets, setExecBullets] = useState([])

  // Live Discovered Infrastructure Data & Schema
  const [instances, setInstances] = useState([])
  const [alarms, setAlarms] = useState([])
  const [logSummaryRows, setLogSummaryRows] = useState([])
  const [sslRows, setSslRows] = useState([])
  const [observations, setObservations] = useState([])
  const [actionItems, setActionItems] = useState([])
  const [httpMetrics, setHttpMetrics] = useState([])
  const [slowQueryMetrics, setSlowQueryMetrics] = useState({})
  const [phpExceptions, setPhpExceptions] = useState({})
  const [applicationIssues, setApplicationIssues] = useState([])
  const [histData, setHistData] = useState([])
  const [rawLogs, setRawLogs] = useState('')

  // Multi-Log Files state
  const [rawFiles, setRawFiles] = useState([])
  const [uploadedFilesMeta, setUploadedFilesMeta] = useState([])
  const [analyzingWithAI, setAnalyzingWithAI] = useState(false)
  const [aiStatusMessage, setAiStatusMessage] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef(null)

  // Multi-Screenshots state
  const [screenshots, setScreenshots] = useState([])
  const [uploadingScreenshots, setUploadingScreenshots] = useState(false)
  const [previewImage, setPreviewImage] = useState(null)
  const imageInputRef = useRef(null)

  // Generation state
  const [generating, setGenerating] = useState(false)
  const [generatedReport, setGeneratedReport] = useState(null)
  const [errorMsg, setErrorMsg] = useState(null)
  const [successToast, setSuccessToast] = useState(null)
  const [aiAnalysisMeta, setAiAnalysisMeta] = useState(null)

  // Save API key to localStorage when changed
  const saveApiKey = (key) => {
    setGeminiApiKey(key)
    localStorage.setItem('cloudiq.gemini_api_key', key.trim())
    setSuccessToast('Gemini API key saved.')
  }

  // Auto-dismiss toast
  useEffect(() => {
    if (successToast) {
      const timer = setTimeout(() => setSuccessToast(null), 6000)
      return () => clearTimeout(timer)
    }
  }, [successToast])

  // Load clients list on mount
  useEffect(() => {
    getClients().then(d => {
      if (d && d.clients && d.clients.length > 0) {
        setClients(d.clients)
        const first = d.clients[0]
        setSelectedClientId(first.id)
        setClientName(first.client_name || '')
        setAccountId(first.account_id || '')
      }
    }).catch(() => {})
  }, [])

  // Quick Client Selector Handler
  const handleClientSelect = (clientId) => {
    setSelectedClientId(clientId)
    const found = clients.find(c => String(c.id) === String(clientId))
    if (found) {
      setClientName(found.client_name || '')
      setAccountId(found.account_id || '')
    }
  }

  // Multi-Log File Selection & Drag-and-Drop
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    addFilesToQueue(files)
  }

  const addFilesToQueue = (files) => {
    setRawFiles(prev => [...prev, ...files])
    const newMeta = files.map((f, idx) => ({
      id: `file-${Date.now()}-${idx}-${Math.random().toString(36).substring(2, 6)}`,
      file: f,
      name: f.name,
      size: f.size,
      type: f.name.split('.').pop().toUpperCase() || 'LOG',
      status: 'queued'
    }))
    setUploadedFilesMeta(prev => [...prev, ...newMeta])
  }

  const removeFileFromQueue = (fileId, fileName) => {
    setUploadedFilesMeta(prev => prev.filter(m => m.id !== fileId))
    setRawFiles(prev => prev.filter(f => f.name !== fileName))
  }

  const clearAllFiles = () => {
    setRawFiles([])
    setUploadedFilesMeta([])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Drag & Drop Handlers
  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true)
    else if (e.type === 'dragleave') setDragActive(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFilesToQueue(Array.from(e.dataTransfer.files))
    }
  }

  // Multi-Screenshot Upload
  const handleScreenshotChange = async (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    setUploadingScreenshots(true)
    setErrorMsg(null)

    for (const file of files) {
      try {
        const reader = new FileReader()
        await new Promise((resolve) => {
          reader.onload = async (event) => {
            const base64 = event.target.result
            try {
              const res = await uploadScreenshot(base64, file.name)
              setScreenshots(prev => [...prev, {
                id: `ss-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
                filename: file.name,
                preview: base64,
                file_path: res.file_path,
                url: res.url,
                caption: file.name.replace(/\.[^/.]+$/, '')
              }])

              if (res.extracted_instances && res.extracted_instances.length > 0) {
                setInstances(prev => {
                  const existing = new Set(prev.map(i => i.name))
                  const additions = res.extracted_instances.filter(i => !existing.has(i.name))
                  return [...prev, ...additions]
                })
              }
              if (res.extracted_alarms && res.extracted_alarms.length > 0) {
                setAlarms(prev => {
                  const existing = new Set(prev.map(a => a.name))
                  const additions = res.extracted_alarms.filter(a => !existing.has(a.name))
                  return [...prev, ...additions]
                })
              }
              if ((res.extracted_instances?.length || 0) > 0 || (res.extracted_alarms?.length || 0) > 0) {
                setSuccessToast(`Vision AI Extracted: ${res.extracted_instances?.length || 0} server node(s) and ${res.extracted_alarms?.length || 0} CloudWatch alarm(s) from "${file.name}"!`)
              }
            } catch (err) {
              setErrorMsg(`Failed uploading screenshot "${file.name}": ${err.message}`)
            }
            resolve()
          }
          reader.readAsDataURL(file)
        })
      } catch (err) {
        setErrorMsg(`Error reading screenshot file: ${err.message}`)
      }
    }
    setUploadingScreenshots(false)
    if (imageInputRef.current) imageInputRef.current.value = ''
  }

  const removeScreenshot = (ssId) => {
    setScreenshots(prev => prev.filter(s => s.id !== ssId))
  }

  // Process Multi-Logs with AI Synthesis
  const handleAnalyzeWithAI = async () => {
    if (rawFiles.length === 0) {
      setErrorMsg('Please select or drop at least one log or telemetry file to analyze.')
      return
    }

    const totalMb = (rawFiles.reduce((acc, f) => acc + (f.size || 0), 0) / (1024 * 1024)).toFixed(1)
    setAnalyzingWithAI(true)
    setAiStatusMessage(`Uploading & synthesizing ${rawFiles.length} file(s) (${totalMb} MB)… please keep this tab open…`)
    setErrorMsg(null)

    try {
      const res = await uploadMultiTelemetryFiles(rawFiles, {
        clientName: clientName || 'Client Infrastructure',
        accountId: accountId || 'AWS Account',
        monitoringPeriod: monitoringPeriod || 'Last 24 Hours',
        geminiApiKey: geminiApiKey.trim()
      })

      // Populate Complete Schema from Engine
      if (res.banner_msg) setBannerMsg(res.banner_msg)
      if (res.alarm_summary) setAlarmSummary(res.alarm_summary)
      if (res.exec_summary) setExecSummary(res.exec_summary)
      if (res.exec_bullets && res.exec_bullets.length > 0) setExecBullets(res.exec_bullets)
      if (res.instances && res.instances.length > 0) {
        setInstances(prev => {
          const hasRealNames = prev.some(i => i.name && !i.name.startsWith('node-') && !i.name.startsWith('ip-'))
          const resHasReal = res.instances.some(i => i.name && !i.name.startsWith('node-') && !i.name.startsWith('ip-'))
          if (hasRealNames && !resHasReal) return prev
          return res.instances
        })
      }
      if (res.alarms && res.alarms.length > 0) {
        setAlarms(prev => (prev.length > 0 && res.alarms.length === 0) ? prev : res.alarms)
      }
      if (res.observations && res.observations.length > 0) setObservations(res.observations)
      if (res.log_summary_rows && res.log_summary_rows.length > 0) setLogSummaryRows(res.log_summary_rows)
      if (res.ssl_rows && res.ssl_rows.length > 0) setSslRows(res.ssl_rows)
      if (res.action_items && res.action_items.length > 0) setActionItems(res.action_items)
      if (res.http_metrics && res.http_metrics.length > 0) setHttpMetrics(res.http_metrics)
      if (res.slow_query_metrics && Object.keys(res.slow_query_metrics).length > 0) setSlowQueryMetrics(res.slow_query_metrics)
      if (res.php_exceptions && Object.keys(res.php_exceptions).length > 0) setPhpExceptions(res.php_exceptions)
      if (res.application_issues && res.application_issues.length > 0) setApplicationIssues(res.application_issues)
      if (res.hist_data && res.hist_data.length > 0) setHistData(res.hist_data)
      if (res.raw_logs) setRawLogs(res.raw_logs)

      setAiAnalysisMeta({
        totalFiles: res.total_files || rawFiles.length,
        totalLines: res.total_log_lines || 0,
        source: res.analysis_source || 'autonomous-engine',
        note: res.analysis_note || '',
        pass1: res.pass1_summary || {}
      })

      setUploadedFilesMeta(prev => prev.map(m => ({ ...m, status: 'processed' })))
      setSuccessToast(`Telemetry synthesis complete: Scanned ${rawFiles.length} file(s) (${(res.total_log_lines || 0).toLocaleString()} records). Report tables populated below.`)

      setTimeout(() => {
        document.getElementById('section-report-schema')?.scrollIntoView({ behavior: 'smooth' })
      }, 150)

    } catch (err) {
      setErrorMsg(`Telemetry Analysis failed: ${err.message}`)
      setUploadedFilesMeta(prev => prev.map(m => ({ ...m, status: 'error' })))
    } finally {
      setAnalyzingWithAI(false)
      setAiStatusMessage('')
    }
  }

  // Dynamic Item Helpers
  const addBullet = () => setExecBullets(prev => [...prev, 'New key highlight / infrastructure milestone'])
  const updateBullet = (index, val) => setExecBullets(prev => prev.map((b, i) => i === index ? val : b))
  const removeBullet = (index) => setExecBullets(prev => prev.filter((_, i) => i !== index))

  const addInstance = () => {
    setInstances(prev => [...prev, {
      name: `node-0${prev.length + 1}`,
      ip: `172.31.10.${prev.length + 10}`,
      cpu_avg: 25.0,
      cpu_peak: 45.0,
      mem_pct: 50.0,
      disk_pct: 40.0,
      status: 'OK'
    }])
  }
  const removeInstance = (index) => setInstances(prev => prev.filter((_, i) => i !== index))

  const addObservation = () => {
    setObservations(prev => [...prev, {
      id: prev.length + 1,
      topic: 'New Infrastructure Observation',
      text: 'Detailed observation notes regarding resource utilization and system stability.',
      priority: 'INFO',
      action_required: false
    }])
  }
  const removeObservation = (index) => setObservations(prev => prev.filter((_, i) => i !== index))

  const addActionItem = () => {
    setActionItems(prev => [...prev, {
      num: prev.length + 1,
      task: 'New remediation task',
      scope: 'Technical scope and execution details',
      priority: 'P2 - MEDIUM'
    }])
  }
  const removeActionItem = (index) => setActionItems(prev => prev.filter((_, i) => i !== index))

  // Final Word + Excel Report Generation
  const handleGenerateReport = async () => {
    setGenerating(true)
    setErrorMsg(null)

    let currentExecSummary = execSummary
    let currentExecBullets = execBullets
    let currentBannerMsg = bannerMsg
    let currentAlarmSummary = alarmSummary
    let currentInstances = instances
    let currentAlarms = alarms
    let currentObservations = observations
    let currentLogSummaryRows = logSummaryRows
    let currentSslRows = sslRows
    let currentActionItems = actionItems
    let currentHttpMetrics = httpMetrics
    let currentSlowQueryMetrics = slowQueryMetrics
    let currentPhpExceptions = phpExceptions
    let currentApplicationIssues = applicationIssues
    let currentHistData = histData
    let currentRawLogs = rawLogs

    // If user uploaded log files but hasn't analyzed them yet, auto-analyze first
    if (rawFiles.length > 0 && (currentInstances.length === 0 || !currentExecSummary || uploadedFilesMeta.some(m => m.status !== 'processed'))) {
      try {
        const res = await uploadMultiTelemetryFiles(rawFiles, {
          clientName: clientName || 'Client Infrastructure',
          accountId: accountId || 'AWS Account',
          monitoringPeriod: monitoringPeriod || 'Last 24 Hours',
          geminiApiKey: geminiApiKey.trim()
        })
        if (res.exec_summary) {
          currentExecSummary = res.exec_summary
          setExecSummary(res.exec_summary)
        }
        if (res.exec_bullets && res.exec_bullets.length > 0) {
          currentExecBullets = res.exec_bullets
          setExecBullets(res.exec_bullets)
        }
        if (res.banner_msg) {
          currentBannerMsg = res.banner_msg
          setBannerMsg(res.banner_msg)
        }
        if (res.alarm_summary) {
          currentAlarmSummary = res.alarm_summary
          setAlarmSummary(res.alarm_summary)
        }
        if (res.instances && res.instances.length > 0) {
          const hasRealNames = currentInstances.some(i => i.name && !i.name.startsWith('node-') && !i.name.startsWith('ip-'))
          const resHasReal = res.instances.some(i => i.name && !i.name.startsWith('node-') && !i.name.startsWith('ip-'))
          if (!hasRealNames || resHasReal) {
            currentInstances = res.instances
            setInstances(res.instances)
          }
        }
        if (res.alarms && res.alarms.length > 0) {
          if (currentAlarms.length === 0 || res.alarms.length > 0) {
            currentAlarms = res.alarms
            setAlarms(res.alarms)
          }
        }
        if (res.observations && res.observations.length > 0) {
          currentObservations = res.observations
          setObservations(res.observations)
        }
        if (res.log_summary_rows && res.log_summary_rows.length > 0) {
          currentLogSummaryRows = res.log_summary_rows
          setLogSummaryRows(res.log_summary_rows)
        }
        if (res.ssl_rows && res.ssl_rows.length > 0) {
          currentSslRows = res.ssl_rows
          setSslRows(res.ssl_rows)
        }
        if (res.action_items && res.action_items.length > 0) {
          currentActionItems = res.action_items
          setActionItems(res.action_items)
        }
        if (res.http_metrics && res.http_metrics.length > 0) {
          currentHttpMetrics = res.http_metrics
          setHttpMetrics(res.http_metrics)
        }
        if (res.slow_query_metrics && Object.keys(res.slow_query_metrics).length > 0) {
          currentSlowQueryMetrics = res.slow_query_metrics
          setSlowQueryMetrics(res.slow_query_metrics)
        }
        if (res.php_exceptions && Object.keys(res.php_exceptions).length > 0) {
          currentPhpExceptions = res.php_exceptions
          setPhpExceptions(res.php_exceptions)
        }
        if (res.application_issues && res.application_issues.length > 0) {
          currentApplicationIssues = res.application_issues
          setApplicationIssues(res.application_issues)
        }
        if (res.hist_data && res.hist_data.length > 0) {
          currentHistData = res.hist_data
          setHistData(res.hist_data)
        }
        if (res.raw_logs) {
          currentRawLogs = res.raw_logs
          setRawLogs(res.raw_logs)
        }
        setUploadedFilesMeta(prev => prev.map(m => ({ ...m, status: 'processed' })))
      } catch (err) {
        console.error('Auto AI analysis during generation error:', err)
        setErrorMsg(`Log telemetry analysis failed: ${err.message}. Please verify the files and retry.`)
        setGenerating(false)
        return
      }
    }

    const payload = {
      client_id: selectedClientId ? parseInt(selectedClientId, 10) : null,
      client_name: clientName || 'Client Infrastructure',
      account_id: accountId || 'AWS Account',
      report_date: reportDate,
      monitoring_period: monitoringPeriod,
      prepared_by: preparedBy,
      banner_msg: currentBannerMsg,
      alarm_summary: currentAlarmSummary,
      exec_summary: currentExecSummary,
      exec_bullets: currentExecBullets,
      instances: currentInstances,
      alarms: currentAlarms,
      log_summary_rows: currentLogSummaryRows,
      ssl_rows: currentSslRows,
      observations: currentObservations,
      action_items: currentActionItems,
      http_metrics: currentHttpMetrics,
      slow_query_metrics: currentSlowQueryMetrics,
      php_exceptions: currentPhpExceptions,
      application_issues: currentApplicationIssues,
      hist_data: currentHistData,
      raw_logs: currentRawLogs,
      screenshot_paths: screenshots.map(s => s.file_path).filter(Boolean)
    }

    try {
      const res = await generateManualReport(payload)
      setGeneratedReport(res)
      setSuccessToast(`Word (.docx) and Excel (.xlsx) reports successfully rendered with Corporate Letterhead!`)
    } catch (err) {
      setErrorMsg(`Report generation failed: ${err.message}`)
    } finally {
      setGenerating(false)
    }
  }

  // Trigger Authenticated Downloads
  const handleDownload = async (docType) => {
    if (!generatedReport?.report_id) return
    try {
      await downloadReport(generatedReport.report_id, docType)
    } catch (err) {
      setErrorMsg(`Download failed: ${err.message}`)
    }
  }

  return (
    <div className="animate-in" style={{ maxWidth: 1280, margin: '0 auto' }}>
      {/* Toast Notification */}
      {successToast && (
        <div className="toast toast-success" style={{
          position: 'fixed',
          top: 24,
          right: 28,
          zIndex: 9999,
          boxShadow: 'var(--shadow-solid-lg)',
          border: '2px solid var(--border-ink)',
          background: '#FFFFFF',
          padding: '12px 18px',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}>
          <CheckCircle2 size={18} style={{ color: '#047857' }} />
          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink-900)' }}>{successToast}</span>
          <button type="button" onClick={() => setSuccessToast(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', marginLeft: 8 }}><X size={14} /></button>
        </div>
      )}

      {/* Error Alert */}
      {errorMsg && (
        <div style={{
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          background: 'var(--brand-coral-bg)',
          border: '2px solid var(--brand-coral)',
          borderRadius: 10,
          boxShadow: 'var(--shadow-solid-sm)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--brand-coral)' }}>
            <AlertTriangle size={18} />
            <span style={{ fontWeight: 600, fontSize: 13 }}>{errorMsg}</span>
          </div>
          <button type="button" onClick={() => setErrorMsg(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--brand-coral)' }}><X size={14} /></button>
        </div>
      )}

      {/* Brand Hero Header Banner */}
      <div style={{
        background: '#FFFFFF',
        border: '2px solid var(--border-ink)',
        borderRadius: 14,
        padding: '20px 24px',
        marginBottom: 24,
        boxShadow: 'var(--shadow-solid-md)',
        display: 'grid',
        gridTemplateColumns: '1fr 280px',
        gap: 20,
        alignItems: 'center'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <img
              src="/logo.png"
              alt="CloudIQ Studio"
              style={{
                width: 44,
                height: 44,
                objectFit: 'contain',
                background: '#FAF8F5',
                borderRadius: 10,
                padding: 3,
                border: '1.5px solid var(--border-ink)',
                boxShadow: '2px 2px 0px var(--border-ink)'
              }}
            />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 800, margin: 0, color: 'var(--ink-900)' }}>
                  CloudIQ Autonomous Report Studio
                </h1>
                <span className="badge badge-ok" style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <ShieldCheck size={11} /> Autonomous CloudOps Engine
                </span>
              </div>
              <p style={{ color: 'var(--ink-600)', fontSize: 13, margin: '2px 0 0 0' }}>
                Upload multiple logs, JSON traces and screenshots. Automatically extracts multi-stream metrics and renders Word & Excel reports with 100% letterhead preservation.
              </p>
            </div>
          </div>
        </div>
        <div style={{ height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Brand3DCanvas style={{ width: '100%', height: '100%' }} />
        </div>
      </div>

      {/* 1. Target Client & Metadata Card */}
      <div className="card" style={{
        padding: 20,
        background: '#FFFFFF',
        border: '2px solid var(--border-ink)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-solid-md)',
        marginBottom: 24
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Server size={18} style={{ color: 'var(--brand-blue)' }} />
            <h2 style={{ fontFamily: 'var(--font-serif)', margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--ink-900)' }}>
              1. Target Client & Report Metadata
            </h2>
          </div>
          {clients.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-600)' }}>Autofill from Client:</span>
              <select
                className="input-select"
                value={selectedClientId}
                onChange={(e) => handleClientSelect(e.target.value)}
                style={{
                  padding: '4px 10px',
                  fontSize: 12.5,
                  border: '1.5px solid var(--border-ink)',
                  borderRadius: 6,
                  fontWeight: 600,
                  boxShadow: 'var(--shadow-solid-sm)'
                }}
              >
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.client_name} ({c.account_id})</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          <div>
            <label className="form-label" style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink-800)', textTransform: 'uppercase' }}>Client Name</label>
            <input
              type="text"
              className="input-text"
              placeholder="e.g. ShyamSteel"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              style={{ border: '1.5px solid var(--border-ink)', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxShadow: 'var(--shadow-solid-sm)' }}
            />
          </div>
          <div>
            <label className="form-label" style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink-800)', textTransform: 'uppercase' }}>Account ID / Identifier</label>
            <input
              type="text"
              className="input-text font-mono"
              placeholder="e.g. 442797924024"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              style={{ border: '1.5px solid var(--border-ink)', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxShadow: 'var(--shadow-solid-sm)' }}
            />
          </div>
          <div>
            <label className="form-label" style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink-800)', textTransform: 'uppercase' }}>Report Date</label>
            <input
              type="text"
              className="input-text"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              style={{ border: '1.5px solid var(--border-ink)', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxShadow: 'var(--shadow-solid-sm)' }}
            />
          </div>
          <div>
            <label className="form-label" style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink-800)', textTransform: 'uppercase' }}>Monitoring Period</label>
            <input
              type="text"
              className="input-text"
              value={monitoringPeriod}
              onChange={(e) => setMonitoringPeriod(e.target.value)}
              style={{ border: '1.5px solid var(--border-ink)', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxShadow: 'var(--shadow-solid-sm)' }}
            />
          </div>
        </div>
      </div>

      {/* 2. Ingestion Zone: Multi-Logs & Screenshots Deck */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Multi-Log Files Box */}
        <div className="card" style={{
          padding: 20,
          background: '#FFFFFF',
          border: '2px solid var(--border-ink)',
          borderRadius: 12,
          boxShadow: 'var(--shadow-solid-md)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileCode size={18} style={{ color: 'var(--brand-blue)' }} />
              <h2 style={{ fontFamily: 'var(--font-serif)', margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--ink-900)' }}>
                2. Log & Telemetry Files ({uploadedFilesMeta.length})
              </h2>
            </div>
            {uploadedFilesMeta.length > 0 && (
              <button type="button" onClick={clearAllFiles} style={{ border: 'none', background: 'transparent', fontSize: 12, fontWeight: 700, color: 'var(--brand-coral)', cursor: 'pointer' }}>
                Clear all
              </button>
            )}
          </div>

          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: '2px dashed var(--border-ink)',
              background: dragActive ? 'var(--brand-blue-subtle)' : 'var(--canvas-bg)',
              borderRadius: 10,
              padding: '24px 16px',
              textAlign: 'center',
              cursor: 'pointer',
              boxShadow: 'var(--shadow-solid-sm)',
              transition: 'all 0.15s ease'
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".log,.txt,.json,.csv,.xlsx,.xls,.md"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <Upload size={28} style={{ color: 'var(--brand-blue)', margin: '0 auto 8px auto' }} />
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink-900)' }}>
              Drop multiple log files here, or <span style={{ color: 'var(--brand-blue)', textDecoration: 'underline' }}>browse</span>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 4 }}>
              Supports CloudWatch Insights JSON exports, syslog, auth.log, nginx/error.log, CSV & Excel
            </div>
          </div>

          {/* Uploaded File Queue List */}
          {uploadedFilesMeta.length > 0 && (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
              {uploadedFilesMeta.map((f) => (
                <div
                  key={f.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    background: 'var(--canvas-bg)',
                    border: '1.5px solid var(--border-ink)',
                    borderRadius: 8,
                    fontSize: 12.5,
                    boxShadow: 'var(--shadow-solid-sm)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="badge badge-neutral" style={{ fontSize: 10, fontWeight: 700 }}>{f.type}</span>
                    <span style={{ fontWeight: 700, color: 'var(--ink-900)' }}>{f.name}</span>
                    <span style={{ color: 'var(--ink-500)', fontSize: 11 }}>({(f.size / 1024).toFixed(1)} KB)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {f.status === 'processed' && <span className="badge badge-ok" style={{ fontSize: 10 }}>Ingested</span>}
                    {f.status === 'queued' && <span className="badge badge-warning" style={{ fontSize: 10 }}>Ready</span>}
                    <button
                      type="button"
                      onClick={() => removeFileFromQueue(f.id, f.name)}
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ink-400)' }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Multi-Screenshot Gallery Box */}
        <div className="card" style={{
          padding: 20,
          background: '#FFFFFF',
          border: '2px solid var(--border-ink)',
          borderRadius: 12,
          boxShadow: 'var(--shadow-solid-md)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ImageIcon size={18} style={{ color: 'var(--brand-blue)' }} />
              <h2 style={{ fontFamily: 'var(--font-serif)', margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--ink-900)' }}>
                3. Screenshots Gallery ({screenshots.length})
              </h2>
            </div>
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              className="btn btn-secondary btn-sm"
              disabled={uploadingScreenshots}
              style={{ border: '1.5px solid var(--border-ink)', borderRadius: 6, fontSize: 12, boxShadow: 'var(--shadow-solid-sm)' }}
            >
              <ImagePlus size={14} /> Add Screenshots
            </button>
          </div>

          <input
            ref={imageInputRef}
            type="file"
            multiple
            accept="image/png,image/jpeg,image/jpg,image/webp"
            onChange={handleScreenshotChange}
            style={{ display: 'none' }}
          />

          {screenshots.length === 0 ? (
            <div
              onClick={() => imageInputRef.current?.click()}
              style={{
                border: '2px dashed var(--border-ink)',
                background: 'var(--canvas-bg)',
                borderRadius: 10,
                padding: '24px 16px',
                textAlign: 'center',
                cursor: 'pointer',
                boxShadow: 'var(--shadow-solid-sm)'
              }}
            >
              <ImageIcon size={28} style={{ color: 'var(--ink-500)', margin: '0 auto 8px auto' }} />
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink-900)' }}>
                Add dashboard or alarm screenshots
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 4 }}>
                PNG, JPG, WebP — automatically linked to the Word report Section 7 appendix
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10, maxHeight: 180, overflowY: 'auto' }}>
              {screenshots.map((s) => (
                <div
                  key={s.id}
                  style={{
                    position: 'relative',
                    border: '1.5px solid var(--border-ink)',
                    borderRadius: 8,
                    overflow: 'hidden',
                    background: '#FFFFFF',
                    boxShadow: 'var(--shadow-solid-sm)',
                    height: 90
                  }}
                >
                  <img
                    src={s.preview}
                    alt={s.caption}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() => setPreviewImage(s)}
                  />
                  <button
                    type="button"
                    onClick={() => removeScreenshot(s.id)}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      background: 'rgba(0,0,0,0.75)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <X size={12} />
                  </button>
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'rgba(18, 24, 38, 0.85)',
                    color: '#fff',
                    fontSize: 10,
                    padding: '2px 4px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontWeight: 600
                  }}>
                    {s.caption}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* AI Synthesis Trigger Card */}
      <div style={{
        background: '#FFFFFF',
        border: '2px solid var(--border-ink)',
        borderRadius: 12,
        padding: '18px 24px',
        marginBottom: 24,
        boxShadow: 'var(--shadow-solid-md)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 16, color: 'var(--ink-900)', fontFamily: 'var(--font-serif)' }}>
            <Sparkles size={20} style={{ color: 'var(--brand-blue)' }} />
            Deep Telemetry Ingestion & Schema Synthesis
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-600)', marginTop: 2 }}>
            {aiStatusMessage || `Scans and aggregates all ${uploadedFilesMeta.length} uploaded file(s), analyzes anomaly patterns, and populates executive summaries & observation tables.`}
          </div>
        </div>

        <button
          type="button"
          onClick={handleAnalyzeWithAI}
          disabled={analyzingWithAI || rawFiles.length === 0}
          className="btn btn-primary"
          style={{
            padding: '12px 28px',
            fontSize: 14,
            fontWeight: 800,
            borderRadius: 10,
            boxShadow: 'var(--shadow-solid-sm)',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          {analyzingWithAI ? (
            <>
              <RefreshCw size={16} className="spin" /> {aiStatusMessage || 'Processing Telemetry…'}
            </>
          ) : (
            <>
              <Zap size={16} /> Synthesize Report Schema
            </>
          )}
        </button>
      </div>

      {/* Telemetry Synthesis Meta Badges */}
      {aiAnalysisMeta && (
        <div style={{
          marginBottom: 16,
          padding: '10px 16px',
          background: 'var(--canvas-bg)',
          border: '1.5px solid var(--border-ink)',
          borderRadius: 8,
          boxShadow: 'var(--shadow-solid-sm)',
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <span className="badge badge-info" style={{ fontWeight: 700 }}>Telemetry Engine: Autonomous CloudOps</span>
          <span className="badge badge-neutral" style={{ fontWeight: 700 }}>Scanned Records: {aiAnalysisMeta.totalLines?.toLocaleString()}</span>
          {aiAnalysisMeta.pass1.CRITICAL !== undefined && (
            <span className={`badge ${aiAnalysisMeta.pass1.CRITICAL > 0 ? 'badge-alarm' : 'badge-ok'}`} style={{ fontWeight: 700 }}>
              Errors: {aiAnalysisMeta.pass1.CRITICAL}
            </span>
          )}
          {aiAnalysisMeta.pass1.SSL_CERTBOT !== undefined && (
            <span className="badge badge-info" style={{ fontWeight: 700 }}>SSL Checks: {aiAnalysisMeta.pass1.SSL_CERTBOT}</span>
          )}
          <span style={{ fontSize: 12, color: 'var(--ink-600)', fontStyle: 'italic' }}>{aiAnalysisMeta.note}</span>
        </div>
      )}

      {/* Multi-Stream Ingested Telemetry Preview */}
      {(httpMetrics.length > 0 || slowQueryMetrics.total > 0 || phpExceptions.total_exceptions > 0) && (
        <div style={{
          marginBottom: 24,
          padding: '14px 18px',
          background: '#FFFFFF',
          border: '2px solid var(--border-ink)',
          borderRadius: 10,
          boxShadow: 'var(--shadow-solid-sm)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <Activity size={16} style={{ color: 'var(--brand-blue)' }} />
            <span style={{ fontWeight: 800, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-800)' }}>
              Deep Multi-Stream Telemetry Metrics (Bridge to Section 3 of Word Report)
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <div style={{ padding: '8px 12px', background: 'var(--canvas-bg)', borderRadius: 6, border: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-600)', textTransform: 'uppercase' }}>HTTP Traffic Scanned</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-900)' }}>
                {httpMetrics.reduce((s, r) => s + (r.count || 0), 0).toLocaleString()} reqs
              </div>
              <div style={{ fontSize: 11, color: '#047857', fontWeight: 600 }}>
                {httpMetrics[0]?.pct || '—'} 200 OK
              </div>
            </div>
            <div style={{ padding: '8px 12px', background: 'var(--canvas-bg)', borderRadius: 6, border: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-600)', textTransform: 'uppercase' }}>MySQL Slow Queries</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: slowQueryMetrics.total > 0 ? '#B45309' : 'var(--ink-900)' }}>
                {slowQueryMetrics.total?.toLocaleString() || 0} events
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-600)', fontWeight: 600 }}>
                Peak: {slowQueryMetrics.max_time || 0}s | Rows: {slowQueryMetrics.rows_examined_total || '—'}
              </div>
            </div>
            <div style={{ padding: '8px 12px', background: 'var(--canvas-bg)', borderRadius: 6, border: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-600)', textTransform: 'uppercase' }}>PHP Exceptions</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: (phpExceptions.total_exceptions || 0) > 0 ? '#DC2626' : 'var(--ink-900)' }}>
                {phpExceptions.total_exceptions || 0} occurrences
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-600)', fontWeight: 600 }}>
                {phpExceptions.top_classes?.[0]?.exception_class || 'Zero critical exceptions'}
              </div>
            </div>
            <div style={{ padding: '8px 12px', background: 'var(--canvas-bg)', borderRadius: 6, border: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-600)', textTransform: 'uppercase' }}>Active Alarms State</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: alarmSummary?.includes('0 IN ALARM') ? '#047857' : '#DC2626' }}>
                {alarmSummary || '0 IN ALARM | 8 OK'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-600)', fontWeight: 600 }}>
                Reflected in Section 2 & Header
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 4. Interactive Report Schema Review Deck */}
      <div id="section-report-schema" className="card" style={{
        padding: 22,
        background: '#FFFFFF',
        border: '2px solid var(--border-ink)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-solid-md)',
        marginBottom: 24
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={18} style={{ color: 'var(--brand-blue)' }} />
            <h2 style={{ fontFamily: 'var(--font-serif)', margin: 0, fontSize: 17, fontWeight: 800, color: 'var(--ink-900)' }}>
              4. Report Schema & Executive Findings
            </h2>
          </div>
          <span className="badge badge-neutral" style={{ fontSize: 11, fontWeight: 700 }}>Interactive Editor</span>
        </div>

        {/* Status Banners */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div>
            <label className="form-label" style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink-800)', textTransform: 'uppercase' }}>
              Top Status Banner Message
            </label>
            <input
              type="text"
              className="input-text"
              value={bannerMsg}
              onChange={(e) => setBannerMsg(e.target.value)}
              style={{ border: '1.5px solid var(--border-ink)', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxShadow: 'var(--shadow-solid-sm)' }}
            />
          </div>
          <div>
            <label className="form-label" style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink-800)', textTransform: 'uppercase' }}>
              Alarm Register Summary
            </label>
            <input
              type="text"
              className="input-text"
              value={alarmSummary}
              onChange={(e) => setAlarmSummary(e.target.value)}
              style={{ border: '1.5px solid var(--border-ink)', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxShadow: 'var(--shadow-solid-sm)' }}
            />
          </div>
        </div>

        {/* Executive Summary Area */}
        <div style={{ marginBottom: 22 }}>
          <label className="form-label" style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink-800)', textTransform: 'uppercase' }}>
            Executive Summary Narrative
          </label>
          <textarea
            className="input-textarea"
            rows={4}
            value={execSummary}
            onChange={(e) => setExecSummary(e.target.value)}
            placeholder="Comprehensive executive summary generated by AI or edited manually..."
            style={{
              width: '100%',
              fontSize: 13,
              lineHeight: 1.6,
              border: '1.5px solid var(--border-ink)',
              borderRadius: 8,
              padding: '10px 14px',
              boxShadow: 'var(--shadow-solid-sm)'
            }}
          />
        </div>

        {/* Key Highlights / Bullets */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <label className="form-label" style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink-800)', textTransform: 'uppercase', margin: 0 }}>
              Executive Highlights & Accomplishments ({execBullets.length})
            </label>
            <button
              type="button"
              onClick={addBullet}
              className="btn btn-secondary btn-sm"
              style={{ border: '1.5px solid var(--border-ink)', borderRadius: 6, fontSize: 12, boxShadow: 'var(--shadow-solid-sm)' }}
            >
              <Plus size={13} /> Add Highlight
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {execBullets.map((bullet, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: 'var(--brand-blue)', fontWeight: 800, fontSize: 14 }}>•</span>
                <input
                  type="text"
                  className="input-text"
                  value={bullet}
                  onChange={(e) => updateBullet(idx, e.target.value)}
                  style={{ flex: 1, fontSize: 13, border: '1.5px solid var(--border-ink)', borderRadius: 6, padding: '6px 10px', boxShadow: 'var(--shadow-solid-sm)' }}
                />
                <button type="button" onClick={() => removeBullet(idx)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ink-400)' }}>
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Compute & Server Metrics Table */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Cpu size={16} style={{ color: 'var(--brand-blue)' }} />
              <h3 style={{ fontFamily: 'var(--font-serif)', margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--ink-900)' }}>
                Monitored Instances & Compute Nodes ({instances.length})
              </h3>
            </div>
            <button
              type="button"
              onClick={addInstance}
              className="btn btn-secondary btn-sm"
              style={{ border: '1.5px solid var(--border-ink)', borderRadius: 6, fontSize: 12, boxShadow: 'var(--shadow-solid-sm)' }}
            >
              <Plus size={13} /> Add Instance
            </button>
          </div>

          <div style={{ border: '1.5px solid var(--border-ink)', borderRadius: 8, overflow: 'hidden', boxShadow: 'var(--shadow-solid-sm)' }}>
            <table className="table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: 'var(--canvas-bg)', borderBottom: '1.5px solid var(--border-ink)', textAlign: 'left' }}>
                  <th style={{ padding: '10px 12px', fontWeight: 800 }}>Node Name</th>
                  <th style={{ padding: '10px 12px', fontWeight: 800 }}>IP Address</th>
                  <th style={{ padding: '10px 12px', fontWeight: 800 }}>CPU Avg %</th>
                  <th style={{ padding: '10px 12px', fontWeight: 800 }}>CPU Peak %</th>
                  <th style={{ padding: '10px 12px', fontWeight: 800 }}>Mem %</th>
                  <th style={{ padding: '10px 12px', fontWeight: 800 }}>Disk %</th>
                  <th style={{ padding: '10px 12px', fontWeight: 800 }}>Status</th>
                  <th style={{ width: 40, padding: '10px 12px' }}></th>
                </tr>
              </thead>
              <tbody>
                {instances.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', color: 'var(--ink-500)', padding: 16 }}>
                      No instances listed. Ingest telemetry files or click "+ Add Instance".
                    </td>
                  </tr>
                ) : (
                  instances.map((inst, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--border-subtle)', background: idx % 2 === 0 ? '#FFFFFF' : 'var(--canvas-bg)' }}>
                      <td style={{ padding: '6px 10px' }}>
                        <input
                          type="text"
                          className="input-text"
                          value={inst.name || ''}
                          onChange={(e) => setInstances(prev => prev.map((item, i) => i === idx ? { ...item, name: e.target.value } : item))}
                          style={{ padding: '4px 8px', fontSize: 12, border: '1px solid var(--border-ink)', borderRadius: 4, fontWeight: 700 }}
                        />
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <input
                          type="text"
                          className="input-text font-mono"
                          value={inst.ip || ''}
                          onChange={(e) => setInstances(prev => prev.map((item, i) => i === idx ? { ...item, ip: e.target.value } : item))}
                          style={{ padding: '4px 8px', fontSize: 12, border: '1px solid var(--border-ink)', borderRadius: 4 }}
                        />
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <input
                          type="number"
                          className="input-text"
                          value={inst.cpu_avg ?? ''}
                          onChange={(e) => setInstances(prev => prev.map((item, i) => i === idx ? { ...item, cpu_avg: parseFloat(e.target.value) || 0 } : item))}
                          style={{ padding: '4px 8px', fontSize: 12, width: 65, border: '1px solid var(--border-ink)', borderRadius: 4 }}
                        />
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <input
                          type="number"
                          className="input-text"
                          value={inst.cpu_peak ?? ''}
                          onChange={(e) => setInstances(prev => prev.map((item, i) => i === idx ? { ...item, cpu_peak: parseFloat(e.target.value) || 0 } : item))}
                          style={{ padding: '4px 8px', fontSize: 12, width: 65, border: '1px solid var(--border-ink)', borderRadius: 4 }}
                        />
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <input
                          type="number"
                          className="input-text"
                          value={inst.mem_pct ?? ''}
                          onChange={(e) => setInstances(prev => prev.map((item, i) => i === idx ? { ...item, mem_pct: parseFloat(e.target.value) || 0 } : item))}
                          style={{ padding: '4px 8px', fontSize: 12, width: 65, border: '1px solid var(--border-ink)', borderRadius: 4 }}
                        />
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <input
                          type="number"
                          className="input-text"
                          value={inst.disk_pct ?? ''}
                          onChange={(e) => setInstances(prev => prev.map((item, i) => i === idx ? { ...item, disk_pct: parseFloat(e.target.value) || 0 } : item))}
                          style={{ padding: '4px 8px', fontSize: 12, width: 65, border: '1px solid var(--border-ink)', borderRadius: 4 }}
                        />
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <select
                          className="input-select"
                          value={inst.status || 'OK'}
                          onChange={(e) => setInstances(prev => prev.map((item, i) => i === idx ? { ...item, status: e.target.value } : item))}
                          style={{ padding: '4px 8px', fontSize: 12, border: '1px solid var(--border-ink)', borderRadius: 4, fontWeight: 700 }}
                        >
                          <option value="OK">OK</option>
                          <option value="IN ALARM">IN ALARM</option>
                          <option value="WARNING">WARNING</option>
                        </select>
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <button type="button" onClick={() => removeInstance(idx)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ink-400)' }}>
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Categorized Observations Deck */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShieldCheck size={16} style={{ color: 'var(--brand-blue)' }} />
              <h3 style={{ fontFamily: 'var(--font-serif)', margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--ink-900)' }}>
                Categorized Operational Observations ({observations.length})
              </h3>
            </div>
            <button
              type="button"
              onClick={addObservation}
              className="btn btn-secondary btn-sm"
              style={{ border: '1.5px solid var(--border-ink)', borderRadius: 6, fontSize: 12, boxShadow: 'var(--shadow-solid-sm)' }}
            >
              <Plus size={13} /> Add Observation
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {observations.map((obs, idx) => (
              <div
                key={idx}
                style={{
                  border: '1.5px solid var(--border-ink)',
                  borderLeft: `5px solid ${obs.priority === 'CRITICAL' ? 'var(--brand-coral)' : (obs.priority === 'WATCH' ? 'var(--brand-yellow)' : 'var(--brand-blue)')}`,
                  borderRadius: 8,
                  padding: 12,
                  background: 'var(--canvas-bg)',
                  boxShadow: 'var(--shadow-solid-sm)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                    <select
                      className="input-select"
                      value={obs.priority || 'INFO'}
                      onChange={(e) => setObservations(prev => prev.map((item, i) => i === idx ? { ...item, priority: e.target.value } : item))}
                      style={{ padding: '3px 8px', fontSize: 11.5, fontWeight: 800, width: 110, border: '1px solid var(--border-ink)', borderRadius: 4 }}
                    >
                      <option value="CRITICAL">CRITICAL</option>
                      <option value="WATCH">WATCH</option>
                      <option value="STABLE">STABLE</option>
                      <option value="INFO">INFO</option>
                    </select>
                    <input
                      type="text"
                      className="input-text"
                      value={obs.topic || ''}
                      onChange={(e) => setObservations(prev => prev.map((item, i) => i === idx ? { ...item, topic: e.target.value } : item))}
                      placeholder="Observation topic..."
                      style={{ fontWeight: 800, fontSize: 13, flex: 1, border: '1px solid var(--border-ink)', borderRadius: 4, padding: '4px 8px' }}
                    />
                  </div>
                  <button type="button" onClick={() => removeObservation(idx)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ink-400)', marginLeft: 12 }}>
                    <Trash2 size={13} />
                  </button>
                </div>
                <textarea
                  className="input-textarea"
                  rows={2}
                  value={obs.text || ''}
                  onChange={(e) => setObservations(prev => prev.map((item, i) => i === idx ? { ...item, text: e.target.value } : item))}
                  style={{ width: '100%', fontSize: 12.5, border: '1px solid var(--border-ink)', borderRadius: 4, padding: '6px 8px', background: '#FFFFFF' }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Action Items Register */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircle size={16} style={{ color: 'var(--brand-blue)' }} />
              <h3 style={{ fontFamily: 'var(--font-serif)', margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--ink-900)' }}>
                Remediation & Action Items ({actionItems.length})
              </h3>
            </div>
            <button
              type="button"
              onClick={addActionItem}
              className="btn btn-secondary btn-sm"
              style={{ border: '1.5px solid var(--border-ink)', borderRadius: 6, fontSize: 12, boxShadow: 'var(--shadow-solid-sm)' }}
            >
              <Plus size={13} /> Add Action Item
            </button>
          </div>

          <div style={{ border: '1.5px solid var(--border-ink)', borderRadius: 8, overflow: 'hidden', boxShadow: 'var(--shadow-solid-sm)' }}>
            <table className="table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: 'var(--canvas-bg)', borderBottom: '1.5px solid var(--border-ink)', textAlign: 'left' }}>
                  <th style={{ width: 45, padding: '10px 12px', fontWeight: 800 }}>#</th>
                  <th style={{ padding: '10px 12px', fontWeight: 800 }}>Remediation Task</th>
                  <th style={{ padding: '10px 12px', fontWeight: 800 }}>Technical Scope & Action</th>
                  <th style={{ width: 140, padding: '10px 12px', fontWeight: 800 }}>Priority</th>
                  <th style={{ width: 40, padding: '10px 12px' }}></th>
                </tr>
              </thead>
              <tbody>
                {actionItems.map((act, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--border-subtle)', background: idx % 2 === 0 ? '#FFFFFF' : 'var(--canvas-bg)' }}>
                    <td style={{ padding: '6px 10px', fontWeight: 800, color: 'var(--ink-700)', textAlign: 'center' }}>
                      {act.num || idx + 1}
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <input
                        type="text"
                        className="input-text"
                        placeholder="Task headline"
                        value={act.task || act.item || ''}
                        onChange={(e) => setActionItems(prev => prev.map((item, i) => i === idx ? { ...item, task: e.target.value, item: e.target.value } : item))}
                        style={{ padding: '4px 8px', fontSize: 12, border: '1px solid var(--border-ink)', borderRadius: 4, width: '100%' }}
                      />
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <input
                        type="text"
                        className="input-text"
                        placeholder="Specific technical scope & metrics"
                        value={act.scope || act.technical_scope || act.description || ''}
                        onChange={(e) => setActionItems(prev => prev.map((item, i) => i === idx ? { ...item, scope: e.target.value } : item))}
                        style={{ padding: '4px 8px', fontSize: 12, border: '1px solid var(--border-ink)', borderRadius: 4, width: '100%' }}
                      />
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <select
                        className="input-select"
                        value={act.priority || 'P2 - MEDIUM'}
                        onChange={(e) => setActionItems(prev => prev.map((item, i) => i === idx ? { ...item, priority: e.target.value } : item))}
                        style={{ padding: '4px 8px', fontSize: 12, border: '1px solid var(--border-ink)', borderRadius: 4, fontWeight: 700, width: '100%' }}
                      >
                        <option value="P1 - HIGH">P1 - HIGH</option>
                        <option value="P2 - MEDIUM">P2 - MEDIUM</option>
                        <option value="P3 - LOW">P3 - LOW</option>
                        <option value="INFO">INFO</option>
                      </select>
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <button type="button" onClick={() => removeActionItem(idx)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ink-400)' }}>
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 5. Production Document Generation & Download Deck */}
      <div style={{
        background: '#FFFFFF',
        border: '2px solid var(--border-ink)',
        borderRadius: 14,
        padding: '24px 28px',
        textAlign: 'center',
        boxShadow: 'var(--shadow-solid-lg)',
        marginBottom: 32
      }}>
        <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 19, fontWeight: 800, margin: '0 0 8px 0', color: 'var(--ink-900)' }}>
          5. Render & Download Branded Reports
        </h2>
        <p style={{ color: 'var(--ink-600)', fontSize: 13.5, margin: '0 0 20px 0' }}>
          Renders the finalized schema into Word (.docx) with complete Corporate Letterhead preservation and Excel (.xlsx) metrics tracker.
        </p>

        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleGenerateReport}
            disabled={generating}
            className="btn btn-primary btn-lg"
            style={{
              padding: '14px 36px',
              fontSize: 15,
              fontWeight: 800,
              borderRadius: 10,
              boxShadow: 'var(--shadow-solid-md)',
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }}
          >
            {generating ? (
              <>
                <RefreshCw size={18} className="spin" /> Generating Reports…
              </>
            ) : (
              <>
                <Play size={18} /> Render & Generate Reports
              </>
            )}
          </button>

          {generatedReport && (
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={() => handleDownload('docx')}
                className="btn btn-secondary btn-lg"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  border: '2px solid var(--border-ink)',
                  color: 'var(--brand-blue)',
                  fontWeight: 800,
                  boxShadow: 'var(--shadow-solid-sm)',
                  borderRadius: 10
                }}
              >
                <Download size={18} /> Download Word (.docx)
              </button>
              <button
                type="button"
                onClick={() => handleDownload('xlsx')}
                className="btn btn-secondary btn-lg"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  border: '2px solid var(--border-ink)',
                  color: '#047857',
                  fontWeight: 800,
                  boxShadow: 'var(--shadow-solid-sm)',
                  borderRadius: 10
                }}
              >
                <FileSpreadsheet size={18} /> Download Excel (.xlsx)
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Preview Modal for Screenshots */}
      {previewImage && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(18, 24, 38, 0.85)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20
          }}
          onClick={() => setPreviewImage(null)}
        >
          <div style={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '90vh',
            background: '#FFFFFF',
            border: '2px solid var(--border-ink)',
            borderRadius: 12,
            padding: 12,
            boxShadow: 'var(--shadow-solid-lg)'
          }}>
            <img src={previewImage.preview} alt={previewImage.caption} style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8, display: 'block' }} />
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              style={{
                position: 'absolute',
                top: -12,
                right: -12,
                background: '#FFFFFF',
                color: 'var(--ink-900)',
                border: '2px solid var(--border-ink)',
                borderRadius: '50%',
                width: 28,
                height: 28,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'var(--shadow-solid-sm)'
              }}
            >
              <X size={16} />
            </button>
            <div style={{ color: 'var(--ink-900)', textAlign: 'center', marginTop: 8, fontSize: 13, fontWeight: 700 }}>
              {previewImage.caption}
            </div>
          </div>
        </div>
      )}


    </div>
  )
}
